// TRONA - Corporate Navbar (shadcn/ui)
import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import {
  Building2,
  Plus,
  Settings,
  HelpCircle,
  LogOut,
  Menu,
  X,
  ChevronDown,
  User,
  LayoutDashboard,
  FileText,
  CheckCircle2,
  Clock,
  AlertCircle,
  Upload,
} from 'lucide-react';
import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';
import { ROUTES } from 'constants/routes';
import { useCompany } from 'contexts/CompanyContext';
import { buildLogoutReturnTo } from 'utils/authRedirect';

// Status badge configurations for company selector
const STATUS_CONFIG = {
  delivered: {
    icon: CheckCircle2,
    color: 'text-emerald-600 dark:text-emerald-400',
    label: 'empresas.stages.incorporated',
  },
  completed: {
    icon: CheckCircle2,
    color: 'text-emerald-600 dark:text-emerald-400',
    label: 'empresas.stages.incorporated',
  },
  dissolved: {
    icon: AlertCircle,
    color: 'text-red-600 dark:text-red-400',
    label: 'empresas.stages.dissolved',
  },
  filed: {
    icon: FileText,
    color: 'text-blue-600 dark:text-blue-400',
    label: 'empresas.stages.filed',
  },
  filing: {
    icon: Clock,
    color: 'text-amber-600 dark:text-amber-400',
    label: 'empresas.stages.inFormation',
  },
  details_pending: {
    icon: Clock,
    color: 'text-blue-600 dark:text-blue-400',
    label: 'empresas.stages.detailsPending',
  },
  under_review: {
    icon: Clock,
    color: 'text-blue-600 dark:text-blue-400',
    label: 'empresas.stages.underReview',
  },
  approved: {
    icon: CheckCircle2,
    color: 'text-emerald-600 dark:text-emerald-400',
    label: 'import.statusSection.status.approved',
  },
  payment_completed: {
    icon: Clock,
    color: 'text-blue-600 dark:text-blue-400',
    label: 'empresas.stages.paymentCompleted',
  },
  payment_pending: {
    icon: AlertCircle,
    color: 'text-orange-600 dark:text-orange-400',
    label: 'empresas.stages.paymentPending',
  },
  default: {
    icon: Clock,
    color: 'text-neutral-500 dark:text-neutral-400',
    label: 'empresas.stages.inProcess',
  },
};

function getStatusConfig(status, dissolutionStatus) {
  if ((dissolutionStatus || '').toLowerCase() === 'completed') {
    return STATUS_CONFIG.dissolved;
  }
  const normalizedStatus = status?.toLowerCase?.() || '';
  return STATUS_CONFIG[normalizedStatus] || STATUS_CONFIG.default;
}

const Navbar = () => {
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const userMenuRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const companyDropdownRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { logout, user } = useAuth0();
  const {
    companies,
    selectedCompany,
    setSelectedCompany,
    selectedCompanyId,
    hasCompanies,
  } = useCompany();

  const returnTo = buildLogoutReturnTo();

  // Close menus on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      if (
        companyDropdownRef.current &&
        !companyDropdownRef.current.contains(event.target)
      ) {
        setShowCompanyDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setShowMobileMenu(false);
  }, [location.pathname]);

  const handleLogout = () => {
    setShowLogoutConfirm(false);
    logout({ logoutParams: { returnTo } });
  };

  const navLinks = [
    {
      path: ROUTES.HOME,
      labelKey: 'navigation.dashboard',
      icon: Building2,
    },
    {
      path: ROUTES.NUEVA_EMPRESA,
      labelKey: 'navigation.newCompany',
      icon: Plus,
    },
  ];

  // Company-scoped routes (only available when a company is selected)
  const companyRoutes = selectedCompanyId
    ? [
        {
          path: ROUTES.COMPANY_DASHBOARD(selectedCompanyId),
          labelKey: 'navigation.dashboard',
          icon: LayoutDashboard,
        },
        {
          path: ROUTES.COMPANY_DOCUMENTS(selectedCompanyId),
          labelKey: 'navigation.documents',
          icon: FileText,
        },
      ]
    : [];

  // Service routes - Both active when company is selected
  const serviceRoutes = selectedCompanyId
    ? [
        {
          path: ROUTES.COMPANY_BANKING(selectedCompanyId),
          labelKey: 'navigation.bankAccount',
          icon: Building2,
          disabled: false,
        },
      ]
    : [
        {
          path: '#',
          labelKey: 'navigation.bankAccount',
          icon: Building2,
          disabled: true,
        },
      ];

  const isActivePath = (path) => location.pathname === path;
  const isActive = (path) => location.pathname.includes(path);

  // Handle company selection from mobile menu
  const handleSelectCompany = (company) => {
    setSelectedCompany(company.id);
    setShowCompanyDropdown(false);
    setShowMobileMenu(false);

    navigate(ROUTES.COMPANY_DASHBOARD(company.id));
  };

  // Get company initial for avatar
  const getCompanyInitial = (name) => {
    if (!name) return '?';
    return name.charAt(0).toUpperCase();
  };

  return (
    <>
      <nav className="fixed left-0 right-0 top-0 z-40 border-b border-white/70 bg-white/80 shadow-[0_14px_40px_-32px_rgba(13,15,26,0.45)] backdrop-blur-xl dark:border-neutral-800/80 dark:bg-neutral-950/80 dark:shadow-black/30 xl:hidden">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            {/* Logo */}
            <div className="flex items-center">
              <Link
                to={ROUTES.HOME}
                className="flex h-9 items-center rounded-xl px-2 transition-all duration-200 dark:bg-white/95 dark:shadow-[0_12px_28px_-22px_rgba(255,255,255,0.85)] dark:ring-1 dark:ring-white/20 sm:h-10 sm:px-2.5"
              >
                <img
                  src="/brand/full-logo.png"
                  alt="TRONA"
                  className="h-6 w-auto object-contain transition-all duration-200 sm:h-7 md:h-8"
                />
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex md:items-center md:gap-1">
              {navLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={cn(
                      'flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200',
                      isActivePath(link.path)
                        ? 'bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400'
                        : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-neutral-200'
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    {t(link.labelKey)}
                  </Link>
                );
              })}
            </div>

            {/* Desktop User Menu */}
            <div className="hidden md:flex md:items-center md:gap-3">
              {/* Help Link */}
              <Link
                to={ROUTES.SOPORTE}
                className="flex h-9 w-9 items-center justify-center rounded-lg text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-700 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-neutral-200"
              >
                <HelpCircle className="h-5 w-5" />
              </Link>

              {/* Settings Link */}
              <Link
                to={ROUTES.CUENTA}
                className="flex h-9 w-9 items-center justify-center rounded-lg text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-700 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-neutral-200"
              >
                <Settings className="h-5 w-5" />
              </Link>

              {/* User Dropdown */}
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700"
                >
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-secondary-100 text-secondary-600 dark:bg-secondary-900/30 dark:text-secondary-400">
                    {user?.name ? (
                      <span className="text-xs font-semibold">
                        {user.name.charAt(0).toUpperCase()}
                      </span>
                    ) : (
                      <User className="h-3.5 w-3.5" />
                    )}
                  </div>
                  <span className="hidden max-w-[120px] truncate lg:inline">
                    {user?.name || user?.email || t('user')}
                  </span>
                  <ChevronDown
                    className={cn(
                      'h-4 w-4 transition-transform duration-200',
                      showUserMenu && 'rotate-180'
                    )}
                  />
                </button>

                {/* Dropdown Menu */}
                {showUserMenu && (
                  <div className="absolute right-0 top-full mt-2 w-56 rounded-xl border border-neutral-200 bg-white py-2 shadow-lg dark:border-neutral-700 dark:bg-neutral-900">
                    {/* User Info */}
                    <div className="border-b border-neutral-100 px-4 py-3 dark:border-neutral-800">
                      <p className="truncate text-sm font-medium text-neutral-900 dark:text-white">
                        {user?.name || t('user')}
                      </p>
                      <p className="truncate text-xs text-neutral-500 dark:text-neutral-400">
                        {user?.email}
                      </p>
                    </div>

                    {/* Menu Items */}
                    <div className="py-1">
                      <Link
                        to={ROUTES.CUENTA}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-neutral-700 transition-colors hover:bg-neutral-50 dark:text-neutral-300 dark:hover:bg-neutral-800"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <Settings className="h-4 w-4 text-neutral-400" />
                        {t('settings')}
                      </Link>
                      <Link
                        to={ROUTES.SOPORTE}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-neutral-700 transition-colors hover:bg-neutral-50 dark:text-neutral-300 dark:hover:bg-neutral-800"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <HelpCircle className="h-4 w-4 text-neutral-400" />
                        {t('navigation.helpSupport')}
                      </Link>
                    </div>

                    {/* Logout */}
                    <div className="border-t border-neutral-100 pt-1 dark:border-neutral-800">
                      <button
                        onClick={() => {
                          setShowUserMenu(false);
                          setShowLogoutConfirm(true);
                        }}
                        className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                      >
                        <LogOut className="h-4 w-4" />
                        {t('sidebar.logout')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Mobile Menu Button */}
            <div className="flex md:hidden">
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="flex h-10 w-10 items-center justify-center rounded-lg text-neutral-600 transition-colors hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800"
              >
                {showMobileMenu ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {showMobileMenu && (
          <div
            ref={mobileMenuRef}
            className="max-h-[calc(100vh-4rem)] overflow-y-auto border-t border-neutral-200 bg-white dark:border-neutral-800 dark:bg-neutral-900 md:hidden"
          >
            <div className="px-4 py-4">
              {/* Company Selector */}
              {hasCompanies && (
                <div ref={companyDropdownRef} className="relative mb-4">
                  <button
                    onClick={() => setShowCompanyDropdown(!showCompanyDropdown)}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 transition-all',
                      showCompanyDropdown
                        ? 'border-primary-400 bg-primary-50/50 dark:border-primary-500 dark:bg-primary-900/20'
                        : 'border-neutral-200 bg-white hover:border-primary-300 dark:border-neutral-700 dark:bg-neutral-800'
                    )}
                  >
                    {/* Company Avatar */}
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-primary-500 to-primary-600 text-sm font-semibold text-white shadow-sm">
                      {getCompanyInitial(selectedCompany?.name)}
                    </div>
                    {/* Company Info */}
                    <div className="min-w-0 flex-1 text-left">
                      <p className="truncate text-sm font-medium text-neutral-900 dark:text-white">
                        {selectedCompany?.name || t('sidebar.selectCompany')}
                      </p>
                      {selectedCompany && (
                        <div className="flex items-center gap-1">
                          {(() => {
  const statusConfig = getStatusConfig(
    selectedCompany?.status,
    selectedCompany?.dissolutionStatus
  );
                            const StatusIcon = statusConfig.icon;
                            return (
                              <>
                                <StatusIcon
                                  className={cn('h-3 w-3', statusConfig.color)}
                                />
                                <span
                                  className={cn(
                                    'text-[11px] font-medium',
                                    statusConfig.color
                                  )}
                                >
                                  {t(statusConfig.label)}
                                </span>
                              </>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                    <ChevronDown
                      className={cn(
                        'h-4 w-4 flex-shrink-0 text-neutral-400 transition-transform',
                        showCompanyDropdown && 'rotate-180'
                      )}
                    />
                  </button>

                  {/* Company Dropdown */}
                  {showCompanyDropdown && (
                    <div className="absolute left-0 right-0 top-full z-50 mt-2 rounded-lg border border-neutral-200 bg-white shadow-lg dark:border-neutral-700 dark:bg-neutral-800">
                      <div className="max-h-48 overflow-y-auto p-2">
                        {companies.map((company) => {
                          const isSelected = company.id === selectedCompany?.id;
                                const companyStatus = getStatusConfig(
                                  company.status,
                                  company.dissolutionStatus
                                );
                          const CompanyStatusIcon = companyStatus.icon;
                          return (
                            <button
                              key={company.id}
                              onClick={() => handleSelectCompany(company)}
                              className={cn(
                                'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 transition-colors',
                                isSelected
                                  ? 'bg-primary-50 dark:bg-primary-900/30'
                                  : 'hover:bg-neutral-100 dark:hover:bg-neutral-700'
                              )}
                            >
                              <div
                                className={cn(
                                  'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-sm font-semibold',
                                  isSelected
                                    ? 'bg-gradient-to-br from-primary-500 to-primary-600 text-white'
                                    : 'bg-neutral-100 text-neutral-600 dark:bg-neutral-700 dark:text-neutral-300'
                                )}
                              >
                                {getCompanyInitial(company.name)}
                              </div>
                              <div className="min-w-0 flex-1 text-left">
                                <p
                                  className={cn(
                                    'truncate text-sm font-medium',
                                    isSelected
                                      ? 'text-primary-600 dark:text-primary-400'
                                      : 'text-neutral-900 dark:text-white'
                                  )}
                                >
                                  {company.name}
                                </p>
                                <div className="flex items-center gap-1">
                                  <CompanyStatusIcon
                                    className={cn(
                                      'h-3 w-3',
                                      companyStatus.color
                                    )}
                                  />
                                  <span
                                    className={cn(
                                      'text-[11px]',
                                      companyStatus.color
                                    )}
                                  >
                                    {t(companyStatus.label)}
                                  </span>
                                </div>
                              </div>
                              {isSelected && (
                                <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-primary-500" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Create New Company Button */}
              <Link
                to={ROUTES.NUEVA_EMPRESA}
                className="mb-2 flex w-full items-center justify-center gap-2 rounded-lg bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
              >
                <Plus className="h-4 w-4" />
                {t('navigation.createCompany')}
              </Link>

              {/* Import Company Button */}
              <Link
                to={ROUTES.IMPORT_EMPRESA}
                className="mb-4 flex w-full items-center justify-center gap-2 rounded-lg border border-neutral-300 bg-white px-4 py-2.5 text-sm font-semibold text-neutral-700 transition-colors hover:bg-neutral-50 dark:border-neutral-600 dark:bg-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-700"
              >
                <Upload className="h-4 w-4" />
                {t('navigation.importCompany')}
              </Link>

              {/* Company Navigation - Only show when a company is selected */}
              {hasCompanies && companyRoutes.length > 0 && (
                <>
                  <p className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-wider text-neutral-400">
                    {selectedCompany?.name}
                  </p>
                  <div className="mb-4 space-y-1">
                    {companyRoutes.map((route) => {
                      const Icon = route.icon;
                      return (
                        <Link
                          key={route.path}
                          to={route.path}
                          className={cn(
                            'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                            isActive(route.path.split('/').pop())
                              ? 'bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400'
                              : 'text-neutral-600 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800'
                          )}
                        >
                          <Icon className="h-5 w-5" />
                          {t(route.labelKey)}
                        </Link>
                      );
                    })}
                  </div>
                </>
              )}

              {/* Services Section */}
              <p className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-wider text-neutral-400">
                {t('navigation.services')}
              </p>
              <div className="mb-4 space-y-1">
                {serviceRoutes.map((route) => {
                  const Icon = route.icon;
                  if (route.disabled) {
                    return (
                      <div
                        key={route.path}
                        className="flex cursor-not-allowed items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-neutral-400 opacity-60"
                      >
                        <Icon className="h-5 w-5" />
                        <span className="flex-1">{t(route.labelKey)}</span>
                        {route.badge && (
                          <span className="rounded-full bg-neutral-200 px-2 py-0.5 text-[10px] font-semibold text-neutral-500 dark:bg-neutral-700 dark:text-neutral-400">
                            Soon
                          </span>
                        )}
                      </div>
                    );
                  }
                  return (
                    <Link
                      key={route.path}
                      to={route.path}
                      className={cn(
                        'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                        isActive(route.path.split('/').pop())
                          ? 'bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400'
                          : 'text-neutral-600 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800'
                      )}
                    >
                      <Icon className="h-5 w-5" />
                      <span className="flex-1">{t(route.labelKey)}</span>
                    </Link>
                  );
                })}
              </div>

              <div className="my-3 h-px bg-neutral-200 dark:bg-neutral-800" />

              {/* Account Section */}
              <p className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-wider text-neutral-400">
                {t('navigation.account')}
              </p>
              <div className="mb-4 space-y-1">
                <Link
                  to={ROUTES.CUENTA}
                  className={cn(
                    'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                    isActive('cuenta')
                      ? 'bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400'
                      : 'text-neutral-600 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800'
                  )}
                >
                  <Settings className="h-5 w-5" />
                  {t('settings')}
                </Link>
                <Link
                  to={ROUTES.SOPORTE}
                  className={cn(
                    'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                    isActive('soporte')
                      ? 'bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400'
                      : 'text-neutral-600 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800'
                  )}
                >
                  <HelpCircle className="h-5 w-5" />
                  {t('navigation.helpSupport')}
                </Link>
              </div>

              <div className="my-3 h-px bg-neutral-200 dark:bg-neutral-800" />

              {/* User Info */}
              {user && (
                <div className="mb-4 flex items-center gap-3 rounded-lg bg-neutral-50 px-3 py-3 dark:bg-neutral-800/50">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-secondary-100 text-secondary-600 dark:bg-secondary-900/30 dark:text-secondary-400">
                    <span className="text-sm font-semibold">
                      {user.name?.charAt(0).toUpperCase() || 'U'}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-neutral-900 dark:text-white">
                      {user.name}
                    </p>
                    <p className="truncate text-xs text-neutral-500 dark:text-neutral-400">
                      {user.email}
                    </p>
                  </div>
                </div>
              )}

              {/* Logout Button */}
              <button
                onClick={() => {
                  setShowMobileMenu(false);
                  setShowLogoutConfirm(true);
                }}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-medium text-red-600 transition-colors hover:bg-red-100 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30"
              >
                <LogOut className="h-4 w-4" />
                {t('sidebar.logout')}
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/60 backdrop-blur-sm">
          <div className="mx-4 w-full max-w-sm rounded-2xl border border-neutral-200 bg-white p-6 shadow-xl dark:border-neutral-800 dark:bg-neutral-900">
            <div className="mb-6 text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20">
                <LogOut className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-neutral-900 dark:text-white">
                {t('sidebar.logoutConfirmTitle')}
              </h3>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                {t('sidebar.logoutConfirmMessage')}
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowLogoutConfirm(false)}
              >
                {t('cancel')}
              </Button>
              <Button
                variant="default"
                className="flex-1 bg-red-600 text-white hover:bg-red-700"
                onClick={handleLogout}
              >
                {t('sidebar.logout')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
